'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { vi } from 'date-fns/locale'
import {
  Calculator,
  Code,
  Edit,
  ImageIcon,
  MessageCircle,
  MoreHorizontal,
  Send,
  Trash2,
  User,
  UserX,
  X,
} from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import * as React from 'react'
import { useRef } from 'react'

import 'katex/dist/katex.min.css'

import { cn } from '@/lib/utils'
import { toast } from '@/hooks/use-toast'
import { useDebounce } from '@/hooks/useDebounce'

import { ClickableHashtagContent } from '@/components/common/clickable-hashtag-content'
import { PostOptionsDropdown } from '@/components/common/post-options-dropdown'
import { ResolvedAvatarImage } from '@/components/common/resolved-media-image'
import { SocialShareButtons } from '@/components/common/social-share-buttons'
import ViewTracker from '@/components/common/view-tracker'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'

import { useAuth } from '@/contexts/auth-context'
import { useMemePostModal } from '@/contexts/meme-post-modal-context'
import { channelUnreadService } from '@/services/channel-unread.service'
import memeService from '@/services/meme.service'
import uploadService from '@/services/upload.service'
import userService from '@/services/user.service'
import { isAdmin, isInstructor } from '@/utils/admin-utils'
import { ErrorResponse } from '@/utils/axios-error-handle'
import { normalizeForumMentionHtmlForEditor } from '@/utils/forum-mention'
import { generateMemeSEO } from '@/utils/seo-utils'

import CodeBlockDialog from './CodeBlockDialog'
import ImageMemeSlideShow from './image-slideshow'
import MathDialog from './MathDialog'
import MemeCommentReactions from './meme-comment-reactions'
import MemePostEditorContainer from './meme-post-editor-container'
import MemePostReactions from './meme-post-reactions'
import { EngagementSummaryBar } from './post-meme-card'
import { CommentsSkeleton, LoadMoreCommentsSkeleton } from './post-skeleton-item'
import QuillWrapper from './QuillWrapper'
import { ClickableHtmlContent } from '../common/clickable-html-content'
import { UserAvatarPopover } from '../common/modal/avatar-hover'
import SEO from '../seo/SEO'
import { Badge } from '../ui/badge'
import { EmojiPicker } from '../ui/emoji-picker'
import { Switch } from '../ui/switch'

import { CommentMeme, CommentMemeReaction, EmojiData, MemePost, PinnedPost } from '@/types/meme.interface'
import { UserProfile } from '@/types/user.type'

interface MentionableUser {
  id: number
  displayName: string
  avatar?: string
  profileId: string
}

export function getDisplayName({ firstName, lastName }: { firstName?: string; lastName?: string }) {
  if (!firstName && !lastName) {
    return 'Người dùng ẩn danh'
  }
  return firstName + ' ' + lastName || 'Người dùng ẩn danh'
}

function CommentItem({
  comment,
  isReply = false,
  level = 0,
  postId,
}: {
  comment: CommentMeme
  isReply?: boolean
  level?: number
  postId: string
}) {
  const [reactions, setReactions] = React.useState<CommentMemeReaction[]>(comment.reactions || [])
  const [showReplyComposer, setShowReplyComposer] = React.useState(false)
  const [showMoreReplies, setShowMoreReplies] = React.useState(false)
  // Edit comment
  const [isEditing, setIsEditing] = React.useState(false)
  const [editContent, setEditContent] = React.useState(comment.content)
  const [editImages, setEditImages] = React.useState<string[]>([])
  const [newImageFile, setNewImageFile] = React.useState<File | null>(null)
  const [newImagePreview, setNewImagePreview] = React.useState<string | null>(null)
  const [keepExistingImage, setKeepExistingImage] = React.useState(true)
  const [isUpdating, setIsUpdating] = React.useState(false)
  const [isUploadingImage, setIsUploadingImage] = React.useState(false)

  const [isMathDialogOpen, setIsMathDialogOpen] = React.useState(false)
  const [isCodeBlockDialogOpen, setIsCodeBlockDialogOpen] = React.useState(false)
  const [editingMathNode, setEditingMathNode] = React.useState<HTMLElement | null>(null)
  const [editingMathLatex, setEditingMathLatex] = React.useState('')
  const [editingCodeBlockNode, setEditingCodeBlockNode] = React.useState<HTMLElement | null>(null)
  const [editingCodeBlockCode, setEditingCodeBlockCode] = React.useState('')
  const [editingCodeBlockLanguage, setEditingCodeBlockLanguage] = React.useState('javascript')

  const fileInputRef = React.useRef<HTMLInputElement>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editQuillRef = React.useRef<any>(null)

  const handleEditEmojiSelect = async (memeData: EmojiData) => {
    const editor = editQuillRef.current?.getEditor?.()
    if (editor) {
      const range = editor.getSelection(true)
      const index = range ? range.index : editor.getLength()
      editor.insertEmbed(
        index,
        'customEmoji',
        {
          src: memeData.imageUrl || '',
          alt: memeData.name,
          name: memeData.name,
        },
        'user'
      )
      editor.setSelection(index + 1, 0, 'silent')
      editor.focus()
      setEditContent(editor.root.innerHTML)
    }
  }

  const handleEditRegularEmojiSelect = (emoji: string) => {
    const editor = editQuillRef.current?.getEditor?.()
    if (!editor) {
      setEditContent(prev => prev + emoji)
      return
    }
    const range = editor.getSelection(true)
    const index = range ? range.index : editor.getLength()

    if (emoji.startsWith('<img')) {
      editor.clipboard.dangerouslyPasteHTML(index, emoji)
      editor.setSelection(index + 1)
    } else {
      editor.insertText(index, emoji)
      editor.setSelection(index + emoji.length)
    }
    editor.focus()
  }

  const handleMathInsert = async (latex: string) => {
    const editor = editQuillRef.current?.getEditor?.()
    if (editor) {
      const mathModule = editor.getModule('math')
      if (editingMathNode && mathModule?.updateMath) {
        mathModule.updateMath(editingMathNode, latex)
        setEditingMathNode(null)
        setEditingMathLatex('')
      } else if (mathModule?.insertMath) {
        mathModule.insertMath(latex, true)
      }
    }
    setIsMathDialogOpen(false)
  }

  const handleCodeBlockInsert = (code: string, language: string) => {
    const editor = editQuillRef.current?.getEditor?.()
    if (editor) {
      const mod = editor.getModule('codeBlock')
      if (editingCodeBlockNode && mod?.updateCodeBlock) {
        mod.updateCodeBlock(editingCodeBlockNode, code, language)
        setEditingCodeBlockNode(null)
        setEditingCodeBlockCode('')
        setEditingCodeBlockLanguage('javascript')
      } else if (mod?.insertCodeBlock) {
        mod.insertCodeBlock(code, language)
      }
    }
    setIsCodeBlockDialogOpen(false)
  }

  const maxLevel = 3 // Maximum nesting level
  const { user } = useAuth()
  const isMaxLevel = level > maxLevel
  const queryClient = useQueryClient()

  React.useEffect(() => {
    const handleGetReaction = async () => {
      if (!user) return
      try {
        const commentDetail = await memeService.getCommentDetail(comment.id)
        setReactions(commentDetail.reactions)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to get comment detail:', error)
      }
    }
    handleGetReaction()
  }, [comment.id, user])

  React.useEffect(() => {
    if (!isEditing) return

    const handleMathEdit = (e: Event) => {
      const detail = (e as CustomEvent).detail as { node: HTMLElement; latex: string }
      setEditingMathNode(detail.node)
      setEditingMathLatex(detail.latex || '')
      setIsMathDialogOpen(true)
    }

    const handleCodeBlockEdit = (e: Event) => {
      const detail = (e as CustomEvent).detail as { node: HTMLElement; code: string; language: string }
      setEditingCodeBlockNode(detail.node)
      setEditingCodeBlockCode(detail.code || '')
      setEditingCodeBlockLanguage(detail.language || 'javascript')
      setIsCodeBlockDialogOpen(true)
    }

    window.addEventListener('quill-math-edit', handleMathEdit)
    window.addEventListener('quill-codeblock-edit', handleCodeBlockEdit)

    return () => {
      window.removeEventListener('quill-math-edit', handleMathEdit)
      window.removeEventListener('quill-codeblock-edit', handleCodeBlockEdit)
    }
  }, [isEditing])

  const stripImagesFromHtml = (htmlContent: string) => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')

    doc.querySelectorAll('img').forEach(img => {
      const parent = img.parentElement
      if (parent && parent.tagName === 'DIV' && parent.children.length === 1) {
        parent.remove()
      } else {
        img.remove()
      }
    })

    return doc.body.innerHTML
  }

  const normalizeQuillHtmlForEdit = (htmlContent: string) => {
    const withoutImages = stripImagesFromHtml(htmlContent)
    const normalized = normalizeForumMentionHtmlForEditor(withoutImages)
      .replace(/<p><br><\/p>/gi, '')
      .replace(/^(<p>\s*<\/p>)+/gi, '')
      .trim()

    return normalized || '<p><br></p>'
  }

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setNewImageFile(file)
    setKeepExistingImage(false)

    const reader = new FileReader()
    reader.onload = e => {
      setNewImagePreview(e.target?.result as string)
    }
    reader.readAsDataURL(file)
  }

  const removeNewImage = () => {
    setNewImageFile(null)
    setNewImagePreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const { mutate: deleteComment, isPending: isDeleting } = useMutation({
    mutationFn: async () => {
      return memeService.deleteComment(comment.id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', postId] })
      // TODO: refactor this, we should not invalidate all meme-post-list
      queryClient.invalidateQueries({ queryKey: ['meme-post-list'] })
    },
    onError: error => {
      // eslint-disable-next-line no-console
      console.error('Failed to delete comment:', error)
      const _error = error as unknown as ErrorResponse
      toast({
        title: 'Error',
        description: _error.response?.data.message || 'Failed to delete comment',
        variant: 'destructive',
      })
    },
  })

  const { mutate: updateComment } = useMutation({
    mutationFn: async ({ content, imageFile }: { content: string; imageFile?: File | null }) => {
      let finalContent = content

      if (imageFile) {
        setIsUploadingImage(true)
        try {
          const uploadResult = await uploadService.uploadImageComment({ image: imageFile })
          finalContent += `<div class="mt-2"><img src="${uploadResult.url}" alt="Comment image" class="rounded-lg max-h-60 object-contain" /></div>`
        } catch (error) {
          setIsUploadingImage(false)
          throw new Error('Failed to upload image')
        }
        setIsUploadingImage(false)
      } else if (keepExistingImage && editImages.length > 0) {
        finalContent += `<div class="mt-2"><img src="${editImages[0]}" alt="Comment image" class="rounded-lg max-h-60 object-contain" /></div>`
      }

      return memeService.updateComment(comment.id, finalContent)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', postId] })
      setIsEditing(false)
      setIsUpdating(false)
      setEditImages([])
      setNewImageFile(null)
      setNewImagePreview(null)
      setKeepExistingImage(true)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    onError: error => {
      // eslint-disable-next-line no-console
      console.error('Failed to update comment:', error)
      const _error = error as unknown as ErrorResponse
      toast({
        title: 'Error',
        description: _error.response?.data.message || 'Failed to update comment',
        variant: 'destructive',
      })
      setIsUpdating(false)
      setIsUploadingImage(false)
    },
  })

  const { mutate: toggleReactionComment } = useMutation({
    mutationFn: async ({ reactionType, emojiData }: { reactionType: string; emojiData: EmojiData }) => {
      return memeService.toggleCommentReaction(comment.id, reactionType, emojiData)
    },
    onSuccess: async () => {
      const commentDetail = await memeService.getCommentDetail(comment.id)
      setReactions(commentDetail.reactions)
    },
    onError: error => {
      // eslint-disable-next-line no-console
      console.error('Failed to react to comment:', error)
      const _error = error as unknown as ErrorResponse
      toast({
        title: 'Error',
        description: _error.response?.data.message || 'Failed to react to comment',
        variant: 'destructive',
      })
    },
  })

  const handleReact = async (commentId: string, reactionType: string, emojiData: EmojiData) => {
    toggleReactionComment({ reactionType, emojiData })
  }

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    deleteComment()
  }

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setEditContent(normalizeQuillHtmlForEdit(comment.content))
    setIsEditing(true)
  }

  const handleSaveEdit = () => {
    if (!editContent.trim() && !newImageFile && (!keepExistingImage || editImages.length === 0)) {
      return // Don't save if no content and no image
    }
    setIsUpdating(true)
    updateComment({
      content: editContent.trim(),
      imageFile: newImageFile,
    })
  }

  const handleCancelEdit = () => {
    setEditContent(normalizeQuillHtmlForEdit(comment.content))
    setEditImages([])
    setNewImageFile(null)
    setNewImagePreview(null)
    setKeepExistingImage(true)
    setIsEditing(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const toggleKeepExistingImage = () => {
    setKeepExistingImage(!keepExistingImage)
  }

  if (isMaxLevel) return null

  return (
    <div className={`relative flex gap-2 ${isReply ? 'ml-4 sm:ml-8 md:ml-10' : ''}`}>
      {/* Add connection line for replies - hide on very small screens */}
      {isReply && (
        <div
          className="absolute -left-3 sm:-left-5 md:-left-6 top-4 w-3 sm:w-5 md:w-6 border-l-2 border-b-2 h-6 border-gray-200 rounded-bl-xl hidden xs:block"
          style={{
            borderLeftWidth: '2px',
            borderBottomWidth: '2px',
            borderColor: `rgba(209, 213, 219, ${Math.max(0.8 - level * 0.2, 0.3)})`,
          }}
        />
      )}

      <Avatar className="h-6 w-6 sm:h-8 sm:w-8 flex-shrink-0 mt-1">
        <ResolvedAvatarImage avatar={comment.user.avatar} alt={comment.user.firstName} />
        <AvatarFallback className="text-xs">
          {comment.isAnonymous
            ? 'A'
            : comment.user.firstName
                .split(' ')
                .map(n => n[0])
                .join('')}
        </AvatarFallback>
      </Avatar>

      <div className="flex flex-row sm:flex-row gap-1 sm:gap-2 flex-1 group min-w-0">
        <div className="flex flex-col flex-1 min-w-0">
          <div className={`bg-gray-100 rounded-2xl px-3 py-2 ${isReply ? 'bg-opacity-80' : ''} break-words`}>
            {!comment.isAnonymous ? (
              <Link href={`/user/${comment.user?.profileId || ''}`} passHref>
                <p className="font-semibold text-xs sm:text-sm truncate hover:underline">
                  {comment.user.firstName + ' ' + comment.user.lastName}
                </p>
              </Link>
            ) : (
              <div className="font-semibold text-xs sm:text-sm truncate text-gray-600 flex items-center gap-1">
                <div>{getDisplayName({ firstName: comment.user.firstName, lastName: comment.user.lastName })}</div>
                {user?.id === comment.user.id && (
                  <Badge variant="outline" className="text-blue-600 border-blue-600 text-xs">
                    Me
                  </Badge>
                )}
              </div>
            )}

            {/* Content display/edit mode */}
            {isEditing ? (
              <div className="mt-2 space-y-2">
                <div className="relative">
                  <QuillWrapper
                    key={`edit-comment-${comment.id}`}
                    ref={editQuillRef}
                    value={editContent}
                    onChange={setEditContent}
                    placeholder="Edit your comment..."
                    modules={{ toolbar: false, hashtag: {}, math: {}, codeBlock: {}, mention: {} }}
                    formats={[
                      'bold',
                      'italic',
                      'underline',
                      'strike',
                      'link',
                      'blockquote',
                      'hashtag',
                      'customEmoji',
                      'math',
                      'codeBlock',
                      'mention',
                    ]}
                    className="bg-gray-100 overflow-y-auto pl-2 pr-10 rounded-md min-h-[40px]"
                    id="comment-edit-quill"
                    theme="snow"
                  />
                  <div className="absolute right-2 top-2 flex items-center gap-1">
                    <EmojiPicker
                      onEmojiSelect={handleEditRegularEmojiSelect}
                      onCustomEmojiSelect={handleEditEmojiSelect}
                      buttonIconClassName="!size-4"
                      buttonClassName="!size-8 rounded-full"
                      modal
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setIsMathDialogOpen(true)}
                      disabled={isUpdating || isUploadingImage}
                      title="Thêm công thức toán học"
                    >
                      <Calculator className="h-4 w-4 text-gray-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setIsCodeBlockDialogOpen(true)}
                      disabled={isUpdating || isUploadingImage}
                      title="Thêm code block"
                    >
                      <Code className="h-4 w-4 text-gray-500" />
                    </Button>
                  </div>
                </div>

                {/* Existing image management */}
                {editImages.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-600">Current image:</p>
                      <label className="flex items-center gap-1 text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={keepExistingImage}
                          onChange={toggleKeepExistingImage}
                          className="w-3 h-3"
                        />
                        <span className="text-gray-600">Keep image</span>
                      </label>
                    </div>
                    <div className={`relative ${!keepExistingImage ? 'opacity-50' : ''}`}>
                      <Image
                        src={editImages[0]}
                        alt="Comment image"
                        width={200}
                        height={120}
                        className="rounded-lg max-h-64 object-cover border border-gray-200"
                      />
                      {!keepExistingImage && (
                        <div className="absolute inset-0 bg-red-100 bg-opacity-75 rounded-lg flex items-center justify-center">
                          <span className="text-red-600 text-xs font-medium">Will be removed</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* New image upload */}
                {!keepExistingImage && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-600">
                        {editImages.length > 0 ? 'Replace with new image:' : 'Add image:'}
                      </p>
                      {!newImageFile && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUpdating || isUploadingImage}
                          className="text-xs h-6"
                        >
                          <ImageIcon className="h-3 w-3 mr-1" />
                          Choose Image
                        </Button>
                      )}
                    </div>

                    {newImagePreview && (
                      <div className="relative">
                        <Image
                          src={newImagePreview}
                          alt="New image preview"
                          width={200}
                          height={120}
                          className="rounded-lg max-h-32 object-cover border border-gray-200"
                        />
                        <button
                          onClick={removeNewImage}
                          className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
                          disabled={isUpdating || isUploadingImage}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}

                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      accept="image/*"
                      onChange={handleImageSelect}
                    />
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={handleSaveEdit}
                    disabled={
                      (!editContent.trim() && !newImageFile && (!keepExistingImage || editImages.length === 0)) ||
                      isUpdating ||
                      isUploadingImage
                    }
                    className="px-3 py-1 text-xs bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUpdating || isUploadingImage ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isUpdating || isUploadingImage}
                    className="px-3 py-1 text-xs bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <ClickableHtmlContent
                content={comment.content}
                className="text-xs sm:text-sm break-words overflow-hidden"
                clickable={true}
                imageClassName=".clickable-image-wrapper img"
              />
            )}
          </div>

          {/* Action buttons - with reactions */}
          {!isEditing && (
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-1 text-xs text-gray-500">
              <span className="whitespace-nowrap">
                {formatDistanceToNow(comment.createdAt, {
                  addSuffix: true,
                  locale: vi,
                })}
              </span>
              {user && (
                <>
                  <MemeCommentReactions
                    commentId={comment.id}
                    reactions={reactions}
                    onReact={handleReact}
                    currentUser={user}
                  />
                  {maxLevel === level ? null : (
                    <button
                      className="hover:underline whitespace-nowrap"
                      onClick={() => setShowReplyComposer(!showReplyComposer)}
                    >
                      Trả lời
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Reply Composer */}
          {showReplyComposer && !isMaxLevel && (
            <div className="mt-2 relative">
              <div
                className="absolute -left-3 sm:-left-5 md:-left-6 top-0 w-3 sm:w-5 md:w-6 border-l-2 h-full hidden xs:block"
                style={{
                  borderColor: `rgba(209, 213, 219, ${Math.max(0.8 - level * 0.2, 0.3)})`,
                }}
              />
              <ReplyComposer
                postId={postId}
                parentId={comment.id}
                onReplySubmit={() => {
                  setShowReplyComposer(false)
                  setShowMoreReplies(true)
                }}
              />
            </div>
          )}

          {/* Nested Comments */}
          {comment.children && comment.children.length > 0 && (
            <div className="mt-2 space-y-2 relative">
              <div
                className="absolute -left-3 sm:-left-5 md:-left-6 top-0 w-3 sm:w-5 md:w-6 border-l-2 h-full hidden xs:block"
                style={{
                  borderColor: `rgba(209, 213, 219, ${Math.max(0.8 - level * 0.2, 0.3)})`,
                }}
              />
              {showMoreReplies &&
                comment.children.map(reply => (
                  <CommentItem key={reply.id} comment={reply} isReply={true} level={level + 1} postId={postId} />
                ))}
              {!showMoreReplies && (
                <button
                  className="text-blue-600 text-xs sm:text-sm hover:underline ml-6 sm:ml-8 md:ml-10 relative break-words"
                  onClick={() => setShowMoreReplies(true)}
                >
                  <div
                    className="absolute -left-3 sm:-left-5 md:-left-6 top-0 w-3 sm:w-5 md:w-6 border-l-2 h-full hidden xs:block"
                    style={{
                      borderColor: `rgba(209, 213, 219, ${Math.max(0.8 - level * 0.2, 0.3)})`,
                    }}
                  />
                  Xem thêm {comment.children.length} phản hồi
                </button>
              )}
            </div>
          )}
        </div>

        {/* Delete/Edit dropdown - positioned better for mobile */}
        {(!comment.isAnonymous && user?.id === comment.user.id && !isEditing) || isAdmin(user) ? (
          <div className="flex-shrink-0 self-start sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-gray-500 hover:bg-gray-100 rounded-full h-5 w-5 sm:h-6 sm:w-6"
                  disabled={isDeleting}
                >
                  <MoreHorizontal className="h-3 w-3 sm:h-4 sm:w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-32 sm:w-36 z-[999999]">
                <DropdownMenuItem
                  className={cn('text-blue-600 hover:bg-blue-50 cursor-pointer text-xs sm:text-sm', {
                    hidden: user?.id !== comment.user.id,
                  })}
                  onClick={handleEditClick}
                >
                  <div className="flex items-center gap-1 sm:gap-2">
                    <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Edit</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-red-600 hover:bg-red-50 cursor-pointer text-xs sm:text-sm"
                  onClick={handleDeleteClick}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <div className="flex items-center gap-1 sm:gap-2">
                      <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-t-2 border-b-2 border-red-600"></div>
                      <span>Deleting...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span>Delete</span>
                    </div>
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
      </div>

      {/* Math Dialog for comment editing */}
      <MathDialog
        isOpen={isMathDialogOpen}
        onClose={() => {
          setIsMathDialogOpen(false)
          setEditingMathNode(null)
          setEditingMathLatex('')
        }}
        onInsert={handleMathInsert}
        initialValue={editingMathLatex}
        isEditMode={!!editingMathNode}
      />

      {/* Code Block Dialog for comment editing */}
      <CodeBlockDialog
        isOpen={isCodeBlockDialogOpen}
        onClose={() => {
          setIsCodeBlockDialogOpen(false)
          setEditingCodeBlockNode(null)
          setEditingCodeBlockCode('')
          setEditingCodeBlockLanguage('javascript')
        }}
        onInsert={handleCodeBlockInsert}
        initialCode={editingCodeBlockCode}
        initialLanguage={editingCodeBlockLanguage}
        isEditMode={!!editingCodeBlockNode}
      />
      <style jsx>{`
        #comment-edit-quill .ql-editor {
          font-size: 16px !important;
          min-height: 40px !important;
          padding: 8px 0 !important;
        }

        #comment-edit-quill .ql-editor.ql-blank::before {
          left: 5px !important;
        }

        #comment-edit-quill .ql-editor .mention,
        #comment-edit-quill .ql-editor .ql-mention {
          display: inline !important;
          font-weight: 700 !important;
          color: #1d4ed8 !important;
          background: transparent !important;
          text-decoration: none !important;
          cursor: pointer !important;
          margin: 0 !important;
        }

        #comment-edit-quill .ql-editor .mention:hover,
        #comment-edit-quill .ql-editor .ql-mention:hover {
          color: #1d4ed8 !important;
          text-decoration: underline !important;
        }
      `}</style>
    </div>
  )
}

function ReplyComposer({
  postId,
  parentId,
  onReplySubmit,
}: {
  postId: string
  parentId: string
  onReplySubmit: () => void
}) {
  const [replyContent, setReplyContent] = React.useState('')
  const [isUploading, setIsUploading] = React.useState(false)
  const [previewImage, setPreviewImage] = React.useState<string | null>(null)
  const [selectedImageFile, setSelectedImageFile] = React.useState<File | null>(null)
  const [isAnonymous, setIsAnonymous] = React.useState(false)
  const [isMathDialogOpen, setIsMathDialogOpen] = React.useState(false)
  const [isCodeBlockDialogOpen, setIsCodeBlockDialogOpen] = React.useState(false)
  const [contentVersion, setContentVersion] = React.useState(0)
  const [editingMathNode, setEditingMathNode] = React.useState<HTMLElement | null>(null)
  const [editingMathLatex, setEditingMathLatex] = React.useState('')
  const [hasContent, setHasContent] = React.useState(false)

  // Mention functionality state
  const [showMentionDropdown, setShowMentionDropdown] = React.useState(false)
  const [mentionQuery, setMentionQuery] = React.useState('')
  const debouncedMentionQuery = useDebounce(mentionQuery, 300)
  const [mentionPosition, setMentionPosition] = React.useState({ x: 0, y: 0 })
  const [selectedUserIndex, setSelectedUserIndex] = React.useState(0)
  const [_mentionContext, setMentionContext] = React.useState<{
    node: Node | null
    offset: number
  }>({ node: null, offset: 0 })
  const [filteredUsers, setFilteredUsers] = React.useState<MentionableUser[]>([])
  const [taggedUsers, setTaggedUsers] = React.useState<number[]>([])

  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const replyQuillRef = React.useRef<any>(null)

  const queryClient = useQueryClient()
  const { user } = useAuth()
  const searchParams = useSearchParams()

  React.useEffect(() => {
    const onRtMathEdit = (e: Event) => {
      const detail = (e as CustomEvent).detail as { node: HTMLElement; latex: string }
      setEditingMathNode(detail.node)
      setEditingMathLatex(detail.latex || '')
      setIsMathDialogOpen(true)
    }
    window.addEventListener('rt-math-edit', onRtMathEdit)
    return () => window.removeEventListener('rt-math-edit', onRtMathEdit)
  }, [])

  const { data: searchResults = [] } = useQuery({
    queryKey: ['user-search', debouncedMentionQuery],
    queryFn: () => userService.searchUsers({ query: debouncedMentionQuery }),
    enabled: debouncedMentionQuery.length > 0,
    staleTime: 30000,
  })

  React.useEffect(() => {
    if (searchResults) {
      const mentionableUsers: MentionableUser[] = searchResults.map((user: UserProfile) => ({
        id: user.id,
        displayName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        avatar: user.avatar,
        profileId: user.profileId,
      }))
      setFilteredUsers(mentionableUsers)
    }
  }, [searchResults])

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowMentionDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!showMentionDropdown) return

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          setSelectedUserIndex(prev => (prev < filteredUsers.length - 1 ? prev + 1 : 0))
          break
        case 'ArrowUp':
          event.preventDefault()
          setSelectedUserIndex(prev => (prev > 0 ? prev - 1 : filteredUsers.length - 1))
          break
        case 'Enter':
          event.preventDefault()
          if (filteredUsers[selectedUserIndex]) {
            handleInsertMention(filteredUsers[selectedUserIndex])
          }
          break
        case 'Escape':
          event.preventDefault()
          setShowMentionDropdown(false)
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMentionDropdown, filteredUsers, selectedUserIndex])

  const handleEmojiSelect = (emoji: string) => {
    setReplyContent(prev => prev + emoji)
    setContentVersion(prev => prev + 1)
  }

  const handleCustomEmojiSelect = async (memeData: EmojiData) => {
    const editor = replyQuillRef.current?.getEditor?.()
    if (editor) {
      const range = editor.getSelection(true)
      const index = range ? range.index : editor.getLength()
      editor.insertEmbed(
        index,
        'customEmoji',
        {
          src: memeData.imageUrl || '',
          alt: memeData.name,
          name: memeData.name,
        },
        'user'
      )
      editor.setSelection(index + 1, 0, 'silent')
      editor.focus()
      setReplyContent(editor.root.innerHTML)
      setContentVersion(prev => prev + 1)
    } else {
      setReplyContent(
        prev =>
          `${prev || ''}<img src="${memeData.imageUrl || ''}" alt="${memeData.name}" style="width:20px;height:20px;display:inline-block;" />`
      )
      setContentVersion(prev => prev + 1)
    }
  }

  const handleMathInsert = async (latex: string) => {
    const editor = replyQuillRef.current?.getEditor?.()
    if (editor) {
      const mathModule = editor.getModule('math')
      if (editingMathNode && mathModule?.updateMath) {
        mathModule.updateMath(editingMathNode, latex)
        setEditingMathNode(null)
        setEditingMathLatex('')
      } else if (mathModule?.insertMath) {
        mathModule.insertMath(latex, true)
      }
      setContentVersion(prev => prev + 1)
    }
    setIsMathDialogOpen(false)
  }

  const handleCodeBlockInsert = (code: string, language: string) => {
    const editor = replyQuillRef.current?.getEditor?.()
    if (editor) {
      const mod = editor.getModule('codeBlock')
      if (mod?.insertCodeBlock) {
        const range = editor.getSelection(true)
        const startIndex = range ? range.index : editor.getLength()
        mod.insertCodeBlock(code, language)
        const afterIndex = (startIndex || 0) + 1
        editor.insertText(afterIndex, '\n', 'user')
        editor.setSelection(afterIndex + 1, 0, 'silent')
      }
      setContentVersion(prev => prev + 1)
    }
    setIsCodeBlockDialogOpen(false)
  }

  const handleIncrementUnreadCount = React.useCallback((receiverId: number) => {
    try {
      const channelId = searchParams.get('channelId')
      if (!channelId || !user?.id) return
      channelUnreadService.notificationIncrementUnreadCountForUser({
        channelId,
        mentionerId: user.id,
        postId,
        receiverId,
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to increment unread count:', error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleInsertMention = (user: MentionableUser) => {
    const editor = replyQuillRef.current?.getEditor?.()
    if (!editor) return

    const range = editor.getSelection(true)
    if (!range) return

    const index = range.index
    const mentionText = `@${user.displayName}`

    const text = editor.getText(0, index)
    const mentionMatch = text.match(/@(\w*)$/)
    if (mentionMatch) {
      const startIndex = index - mentionMatch[0].length

      editor.deleteText(startIndex, mentionMatch[0].length)

      const mentionModule = editor.getModule('mention')
      if (mentionModule && mentionModule.insertMention) {
        mentionModule.insertMention({
          id: user.profileId.toString(),
          displayName: user.displayName,
          profileId: user.profileId,
        })
      } else {
        const mentionData = {
          id: user.id.toString(),
          displayName: user.displayName,
          text: mentionText,
        }

        editor.insertEmbed(startIndex, 'mention', mentionData)
        editor.insertText(startIndex + 1, ' ')
        editor.setSelection(startIndex + 2)
      }
    }

    editor.focus()
    setReplyContent(editor.root.innerHTML)
    setContentVersion(prev => prev + 1)

    setTaggedUsers(prevSelected => {
      if (prevSelected.some(prevUserId => prevUserId === user.id)) {
        return prevSelected
      }
      return [...prevSelected, user.id]
    })

    setShowMentionDropdown(false)
    setMentionContext({ node: null, offset: 0 })
    handleIncrementUnreadCount(user.id)
  }

  const handleQuillChange = (content: string, delta: unknown, source: string, editor: unknown) => {
    setReplyContent(content)
    setContentVersion(prev => prev + 1)

    if (source === 'user' && editor && typeof editor === 'object') {
      const quillEditor = editor as {
        getSelection: () => { index: number } | null
        getText: (start: number, end: number) => string
        getBounds: (index: number) => { left: number; bottom: number }
      }
      const selection = quillEditor.getSelection()
      if (selection) {
        const text = quillEditor.getText(0, selection.index)
        const mentionMatch = text.match(/@(\w*)$/)

        if (mentionMatch) {
          const query = mentionMatch[1]
          setMentionQuery(query)
          setShowMentionDropdown(true)

          const bounds = quillEditor.getBounds(selection.index)
          setMentionPosition({
            x: bounds.left,
            y: bounds.bottom + 10,
          })
        } else {
          setShowMentionDropdown(false)
        }
      }
    }
  }

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setSelectedImageFile(file)

    const reader = new FileReader()
    reader.onload = e => {
      setPreviewImage(e.target?.result as string)
    }
    reader.readAsDataURL(file)
  }

  const removeImage = () => {
    setPreviewImage(null)
    setSelectedImageFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const { mutate: submitReply, isPending } = useMutation({
    mutationFn: async () => {
      let content = replyContent

      if (selectedImageFile) {
        setIsUploading(true)
        try {
          const result = await uploadService.uploadImageComment({ image: selectedImageFile })
          // Add image HTML to content after successful upload
          content += `<div class="mt-2 clickable-image-wrapper"><img src="${result.url}" alt="Reply image" class="rounded-lg max-h-60 object-contain" /></div>`
        } catch (error) {
          setIsUploading(false)
          throw new Error('Failed to upload image')
        }
        setIsUploading(false)
      }

      return memeService.createComment({
        postId,
        content,
        parentId,
        isAnonymous,
        mentionedUserIds: taggedUsers,
      })
    },
    onSuccess: () => {
      setReplyContent('')
      setTaggedUsers([])
      setPreviewImage(null)
      setSelectedImageFile(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      queryClient.invalidateQueries({ queryKey: ['comments'] })
      onReplySubmit()
    },
    onError: error => {
      // eslint-disable-next-line no-console
      console.error('Failed to submit reply:', error)
      const _error = error as unknown as ErrorResponse
      toast({
        title: 'Error',
        description: _error.response?.data.message || 'Failed to submit reply',
        variant: 'destructive',
      })
    },
  })

  React.useEffect(() => {
    const checkContent = () => {
      const editorHasContent = (replyQuillRef.current?.getEditor?.()?.root?.innerText || '').trim().length > 0
      const imageContent = !!selectedImageFile
      const value = replyContent || ''
      const hasBlocks =
        value.includes('class="math-block"') || value.includes('class="code-block"') || /<img\b/i.test(value)
      const plainText = value.replace(/<[^>]*>/g, '').trim().length > 0
      setHasContent(editorHasContent || imageContent || hasBlocks || plainText)
    }
    checkContent()
  }, [selectedImageFile, contentVersion, replyContent])

  if (!user) {
    return null
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-2">
          {isAnonymous ? <UserX className="h-3 w-3 text-gray-500" /> : <User className="h-3 w-3 text-gray-500" />}
          <Label htmlFor={`reply-anonymous-toggle-${parentId}`} className="text-xs text-gray-600 cursor-pointer">
            Phản hồi ẩn danh
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id={`reply-anonymous-toggle-${parentId}`}
            checked={isAnonymous}
            onCheckedChange={setIsAnonymous}
            disabled={isPending || isUploading}
            className="scale-75"
          />
          <EmojiPicker
            onEmojiSelect={handleEmojiSelect}
            onCustomEmojiSelect={handleCustomEmojiSelect}
            buttonIconClassName="!size-4"
            buttonClassName="!size-8 rounded-full"
            modal
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setIsMathDialogOpen(true)}
            disabled={isPending || isUploading}
            title="Thêm công thức toán học"
          >
            <Calculator className="h-4 w-4 text-gray-500" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setIsCodeBlockDialogOpen(true)}
            disabled={isPending || isUploading}
            title="Thêm code block"
          >
            <Code className="h-4 w-4 text-gray-500" />
          </Button>
          {!selectedImageFile && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => fileInputRef.current?.click()}
              disabled={isPending || isUploading}
              title="Thêm ảnh"
            >
              <ImageIcon className="h-4 w-4 text-gray-500" />
            </Button>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <UserAvatarPopover user={user} />
        <div className="w-full">
          <div className="relative max-w-full">
            <QuillWrapper
              ref={replyQuillRef}
              value={replyContent}
              onChange={handleQuillChange}
              placeholder="Viết phản hồi..."
              modules={{ toolbar: false, hashtag: {}, math: {}, codeBlock: {}, mention: {} }}
              formats={[
                'bold',
                'italic',
                'underline',
                'strike',
                'link',
                'blockquote',
                'hashtag',
                'customEmoji',
                'math',
                'codeBlock',
                'mention',
              ]}
              className="bg-gray-100 overflow-y-auto pr-10 rounded-md fsds-meme-editor max-w-full"
              id="reply-quill"
              theme="snow"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                disabled={isPending || isUploading || !hasContent}
                onClick={() => submitReply()}
                title="Gửi phản hồi"
              >
                {isPending || isUploading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-500"></div>
                ) : (
                  <Send className="h-4 w-4 text-gray-500" />
                )}
              </Button>
              <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageSelect} />
            </div>

            {/* Mention Dropdown */}
            {showMentionDropdown && filteredUsers.length > 0 && (
              <div
                ref={dropdownRef}
                className="absolute z-50 w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                style={{
                  left: `${mentionPosition.x}px`,
                  top: `-260%`,
                }}
              >
                <ScrollArea className="h-full">
                  {filteredUsers.map((user, index) => (
                    <div
                      key={user.id}
                      className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                        index === selectedUserIndex ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => handleInsertMention(user)}
                    >
                      <Avatar className="h-6 w-6">
                        <ResolvedAvatarImage avatar={user.avatar} alt={user.displayName} />
                        <AvatarFallback className="text-xs">{user.displayName.charAt(0).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-gray-700 truncate">{user.displayName}</span>
                    </div>
                  ))}
                </ScrollArea>
              </div>
            )}
          </div>

          {/* Image Preview */}
          {previewImage && (
            <div className="mt-2 relative rounded-lg overflow-hidden bg-gray-50">
              <Image
                src={previewImage}
                alt="Preview"
                width={200}
                height={150}
                className="object-contain rounded-lg border border-gray-200 w-full max-h-32"
              />
              <div className="absolute top-2 right-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 bg-gray-100 hover:bg-gray-200 rounded-full"
                  onClick={removeImage}
                  disabled={isPending || isUploading}
                >
                  <X className="h-3 w-3 text-gray-500" />
                </Button>
              </div>
              {/* Show upload status overlay */}
              {isUploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg">
                  <div className="flex flex-col items-center text-white">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-white mb-1"></div>
                    <span className="text-xs">Đang tải ảnh...</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <MathDialog
        isOpen={isMathDialogOpen}
        onClose={() => {
          setIsMathDialogOpen(false)
          setEditingMathNode(null)
          setEditingMathLatex('')
        }}
        onInsert={handleMathInsert}
        initialValue={editingMathLatex}
        isEditMode={!!editingMathNode}
      />

      <CodeBlockDialog
        isOpen={isCodeBlockDialogOpen}
        onClose={() => setIsCodeBlockDialogOpen(false)}
        onInsert={handleCodeBlockInsert}
      />
      <style jsx>{`
        #reply-quill .ql-editor {
          font-size: 16px !important;
        }

        #reply-quill .ql-editor.ql-blank::before {
          left: 5px !important;
        }

        #reply-quill .ql-editor .mention {
          background-color: #e2e8f0 !important;
          border-radius: 4px !important;
          padding: 2px 4px !important;
          color: #1e293b !important;
          font-weight: 500 !important;
          cursor: pointer !important;
          white-space: nowrap !important;
          display: inline-block !important;
          user-select: none !important;
          margin: 0 !important;
          transition: background-color 0.2s ease !important;
        }

        #reply-quill .ql-editor .mention:hover {
          background-color: #cbd5e1 !important;
          text-decoration: none !important;
        }

        /* Ensure mentions don't get mixed with normal text formatting */
        #reply-quill .ql-editor .mention * {
          background: inherit !important;
          color: inherit !important;
          font-weight: inherit !important;
        }

        /* Hashtag styling */
        #reply-quill .ql-editor .hashtag {
          background-color: #dbeafe !important;
          color: #1e40af !important;
          border-radius: 4px !important;
          padding: 2px 4px !important;
          font-weight: 500 !important;
          cursor: pointer !important;
          white-space: nowrap !important;
          display: inline-block !important;
          user-select: none !important;
          margin: 0 1px !important;
        }

        #reply-quill .ql-editor .hashtag:hover {
          background-color: #bfdbfe !important;
        }

        /* Ensure hashtags don't get mixed with normal text formatting */
        #reply-quill .ql-editor .hashtag * {
          background: inherit !important;
          color: inherit !important;
          font-weight: inherit !important;
        }
      `}</style>
    </div>
  )
}

function CommentArea({ postId }: { postId: string }) {
  const [currentPage, setCurrentPage] = React.useState(1)
  const [allComments, setAllComments] = React.useState<CommentMeme[]>([])
  const [hasMoreComments, setHasMoreComments] = React.useState(true)
  const pageSize = 20

  const {
    data: commentsData,
    isLoading,
    error,
    isFetching,
  } = useQuery({
    queryKey: ['comments', postId, currentPage],
    queryFn: () =>
      memeService.getComments(postId, {
        pageSize,
        pageNumber: currentPage,
      }),
    enabled: !!postId,
  })

  React.useEffect(() => {
    if (commentsData?.data) {
      if (currentPage === 1) {
        setAllComments(commentsData.data)
      } else {
        setAllComments(prev => [...prev, ...commentsData.data])
      }

      // Check if there are more comments to load
      const { currentPage: responsePage, totalPages } = commentsData.meta
      setHasMoreComments(responsePage < totalPages)
    }
  }, [commentsData, currentPage])

  const handleLoadMore = () => {
    if (!isFetching && hasMoreComments) {
      setCurrentPage(prev => prev + 1)
    }
  }

  if (isLoading && currentPage === 1) {
    return <CommentsSkeleton count={5} />
  }

  if (error && currentPage === 1) {
    return (
      <div className="text-center py-4 text-red-500">
        <p>Error loading comments: {error.message}</p>
        <button onClick={() => setCurrentPage(1)} className="mt-2 text-blue-600 hover:underline">
          Try again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {allComments.map(comment => (
        <CommentItem key={comment.id} comment={comment} postId={postId} level={0} />
      ))}

      {/* Loading more comments skeleton */}
      {isFetching && currentPage > 1 && <LoadMoreCommentsSkeleton />}

      {/* Load More Button */}
      {hasMoreComments && !isFetching && (
        <div className="flex justify-center">
          <Button
            variant="link"
            onClick={handleLoadMore}
            disabled={isFetching}
            className="px-6 py-2 text-sm text-blue-600 hover:underline"
          >
            Load more comments
          </Button>
        </div>
      )}

      {/* No comments message */}
      {!hasMoreComments && allComments.length === 0 && !isLoading && (
        <div className="text-center py-8 text-gray-500">
          <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No comments yet. Be the first to comment!</p>
        </div>
      )}
    </div>
  )
}

function CommentComposer({ currentPost }: { currentPost: MemePost }) {
  const { user } = useAuth()
  const [newComment, setNewComment] = React.useState('')
  const [isUploading, setIsUploading] = React.useState(false)
  const [previewImage, setPreviewImage] = React.useState<string | null>(null)
  const [selectedImageFile, setSelectedImageFile] = React.useState<File | null>(null)
  const [isAnonymous, setIsAnonymous] = React.useState(false)
  const [isMathDialogOpen, setIsMathDialogOpen] = React.useState(false)
  const [isCodeBlockDialogOpen, setIsCodeBlockDialogOpen] = React.useState(false)
  const [editingMathNode, setEditingMathNode] = React.useState<HTMLElement | null>(null)
  const [editingMathLatex, setEditingMathLatex] = React.useState('')

  const [showMentionDropdown, setShowMentionDropdown] = React.useState(false)
  const [mentionQuery, setMentionQuery] = React.useState('')
  const debouncedMentionQuery = useDebounce(mentionQuery, 300)
  const [selectedUserIndex, setSelectedUserIndex] = React.useState(0)
  const [_mentionContext, setMentionContext] = React.useState<{
    node: Node | null
    offset: number
  }>({ node: null, offset: 0 })
  const [filteredUsers, setFilteredUsers] = React.useState<MentionableUser[]>([])
  const [taggedUsers, setTaggedUsers] = React.useState<number[]>([])

  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const searchParams = useSearchParams()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const commentQuillRef = useRef<any>(null)

  React.useEffect(() => {
    const onRtMathEdit = (e: Event) => {
      const detail = (e as CustomEvent).detail as { node: HTMLElement; latex: string }
      setEditingMathNode(detail.node)
      setEditingMathLatex(detail.latex || '')
      setIsMathDialogOpen(true)
    }
    window.addEventListener('rt-math-edit', onRtMathEdit)
    return () => window.removeEventListener('rt-math-edit', onRtMathEdit)
  }, [])

  const { data: searchResults = [] } = useQuery({
    queryKey: ['user-search', debouncedMentionQuery],
    queryFn: () => userService.searchUsers({ query: debouncedMentionQuery }),
    enabled: debouncedMentionQuery.length > 0,
    staleTime: 30000,
  })

  React.useEffect(() => {
    if (searchResults) {
      const mentionableUsers: MentionableUser[] = searchResults.map((user: UserProfile) => ({
        id: user.id,
        displayName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        avatar: user.avatar,
        profileId: user.profileId,
      }))
      setFilteredUsers(mentionableUsers)
    }
  }, [searchResults])

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowMentionDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!showMentionDropdown) return

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          setSelectedUserIndex(prev => (prev < filteredUsers.length - 1 ? prev + 1 : 0))
          break
        case 'ArrowUp':
          event.preventDefault()
          setSelectedUserIndex(prev => (prev > 0 ? prev - 1 : filteredUsers.length - 1))
          break
        case 'Enter':
          event.preventDefault()
          if (filteredUsers[selectedUserIndex]) {
            handleInsertMention(filteredUsers[selectedUserIndex])
          }
          break
        case 'Escape':
          event.preventDefault()
          setShowMentionDropdown(false)
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMentionDropdown, filteredUsers, selectedUserIndex])

  const handleEmojiSelect = async (emoji: string) => {
    const editor = commentQuillRef.current?.getEditor?.()
    if (!editor) {
      setNewComment(prev => prev + emoji)
      return
    }
    const range = editor.getSelection(true)
    const index = range ? range.index : editor.getLength()

    if (emoji.startsWith('<img')) {
      editor.clipboard.dangerouslyPasteHTML(index, emoji)
      editor.setSelection(index + 1)
    } else {
      editor.insertText(index, emoji)
      editor.setSelection(index + emoji.length)
    }
    editor.focus()
  }

  const handleCustomEmojiSelect = async (emoji: EmojiData) => {
    const editor = commentQuillRef.current?.getEditor?.()
    if (!editor) {
      // eslint-disable-next-line no-console
      console.warn('Editor not available. Inserting raw HTML fallback.')
      setNewComment(
        prev => `${prev}<img src="${emoji.imageUrl}" alt=":${emoji.name}:" style="width:24px;height:24px;" />`
      )
      return
    }

    try {
      const range = editor.getSelection(true)
      const index = range ? range.index : editor.getLength()
      // eslint-disable-next-line no-console
      console.log('Inserting emoji at index:', index)

      editor.insertEmbed(index, 'customEmoji', {
        src: emoji.imageUrl,
        alt: `:${emoji.name}:`,
        name: emoji.name ?? 'emoji',
      })

      editor.setSelection(index + 1)
      editor.focus()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to insert custom emoji:', error)
    }
  }

  const handleMathInsert = async (latex: string) => {
    const editor = commentQuillRef.current?.getEditor?.()
    if (editor) {
      const mathModule = editor.getModule('math')
      if (editingMathNode && mathModule?.updateMath) {
        mathModule.updateMath(editingMathNode, latex)
        setEditingMathNode(null)
        setEditingMathLatex('')
      } else if (mathModule?.insertMath) {
        mathModule.insertMath(latex, true)
      }
    }
    setIsMathDialogOpen(false)
  }

  const handleCodeBlockInsert = (code: string, language: string) => {
    const editor = commentQuillRef.current?.getEditor?.()
    if (editor) {
      const mod = editor.getModule('codeBlock')
      if (mod?.insertCodeBlock) mod.insertCodeBlock(code, language)
    }
    setIsCodeBlockDialogOpen(false)
  }

  const handleIncrementUnreadCount = React.useCallback((receiverId: number) => {
    try {
      const channelId = searchParams.get('channelId')
      if (!channelId || !user?.id) return
      channelUnreadService.notificationIncrementUnreadCountForUser({
        channelId,
        mentionerId: user.id,
        postId: currentPost.id,
        receiverId,
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to increment unread count:', error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleInsertMention = (user: MentionableUser) => {
    const editor = commentQuillRef.current?.getEditor?.()
    if (!editor) return

    const range = editor.getSelection(true)
    if (!range) return

    const index = range.index
    const mentionText = `@${user.displayName}`

    const text = editor.getText(0, index)
    const mentionMatch = text.match(/@(\w*)$/)
    if (mentionMatch) {
      const startIndex = index - mentionMatch[0].length

      editor.deleteText(startIndex, mentionMatch[0].length)

      const mentionModule = editor.getModule('mention')
      if (mentionModule && mentionModule.insertMention) {
        mentionModule.insertMention({
          id: user.profileId.toString(),
          displayName: user.displayName,
        })
      } else {
        const mentionData = {
          id: user.profileId.toString(),
          displayName: user.displayName,
          text: mentionText,
        }

        editor.insertEmbed(startIndex, 'mention', mentionData)
        editor.insertText(startIndex + 1, ' ')
        editor.setSelection(startIndex + 2)
      }
    }

    editor.focus()

    setTaggedUsers(prevSelected => {
      if (prevSelected.some(prevUserId => prevUserId === user.id)) {
        return prevSelected
      }
      return [...prevSelected, user.id]
    })

    setShowMentionDropdown(false)
    setMentionContext({ node: null, offset: 0 })
    handleIncrementUnreadCount(user.id)
  }

  const handleQuillChange = (content: string, delta: unknown, source: string, editor: unknown) => {
    setNewComment(content)

    if (source === 'user' && editor && typeof editor === 'object') {
      const quillEditor = editor as {
        getSelection: () => { index: number } | null
        getText: (start: number, end: number) => string
        getBounds: (index: number) => { left: number; bottom: number }
      }
      const selection = quillEditor.getSelection()
      if (selection) {
        const text = quillEditor.getText(0, selection.index)
        const mentionMatch = text.match(/@(\w*)$/)

        if (mentionMatch) {
          const query = mentionMatch[1]
          setMentionQuery(query)
          setShowMentionDropdown(true)
        } else {
          setShowMentionDropdown(false)
        }
      }
    }
  }

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setSelectedImageFile(file)

    const reader = new FileReader()
    reader.onload = e => {
      setPreviewImage(e.target?.result as string)
    }
    reader.readAsDataURL(file)
  }

  const removeImage = () => {
    setPreviewImage(null)
    setSelectedImageFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const { mutate: submitComment, isPending } = useMutation({
    mutationFn: async () => {
      let content = newComment

      if (selectedImageFile) {
        setIsUploading(true)
        try {
          const result = await uploadService.uploadImageComment({ image: selectedImageFile })
          content += `<div class="mt-2 clickable-image-wrapper"><img src="${result.url}" alt="Comment image" class="rounded-lg max-h-60 object-contain" /></div>`
        } catch (error) {
          setIsUploading(false)
          throw new Error('Failed to upload image')
        }
        setIsUploading(false)
      }

      return memeService.createComment({
        postId: currentPost.id,
        content,
        isAnonymous,
        mentionedUserIds: taggedUsers,
      })
    },
    onSuccess: () => {
      setNewComment('')
      setTaggedUsers([])
      setPreviewImage(null)
      setSelectedImageFile(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

      queryClient.invalidateQueries({ queryKey: ['comments', currentPost.id] })
    },
    onError: error => {
      // eslint-disable-next-line no-console
      console.error('Failed to submit comment:', error)
      const _error = error as unknown as ErrorResponse
      toast({
        title: 'Error',
        description: _error.response?.data.message || 'Failed to submit comment',
        variant: 'destructive',
      })
    },
  })

  const hasContent = React.useMemo(() => {
    if (selectedImageFile) return true
    const editorHasContent = (commentQuillRef.current?.getEditor?.()?.root?.innerText || '').trim().length > 0
    const value = newComment || ''
    const hasBlocks =
      value.includes('class="math-block"') || value.includes('class="code-block"') || /<img\b/i.test(value)
    const plainText = value.replace(/<[^>]*>/g, '').trim().length > 0
    return editorHasContent || hasBlocks || plainText
  }, [newComment, selectedImageFile])

  if (!user) {
    return null
  }

  return (
    <div className="border-t bg-white p-4 flex-shrink-0">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {isAnonymous ? <UserX className="h-4 w-4 text-gray-500" /> : <User className="h-4 w-4 text-gray-500" />}
          <Label htmlFor="comment-anonymous-toggle" className="text-sm text-gray-700 cursor-pointer">
            Bình luận ẩn danh
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="comment-anonymous-toggle"
            checked={isAnonymous}
            onCheckedChange={setIsAnonymous}
            disabled={isPending || isUploading}
          />
          <EmojiPicker
            onEmojiSelect={handleEmojiSelect}
            onCustomEmojiSelect={handleCustomEmojiSelect}
            buttonIconClassName="!size-5"
            buttonClassName="!size-8 rounded-full"
            modal
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setIsMathDialogOpen(true)}
            disabled={isPending || isUploading}
            title="Thêm công thức toán học"
          >
            <Calculator className="size-4 text-gray-500" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setIsCodeBlockDialogOpen(true)}
            disabled={isPending || isUploading}
            title="Thêm code block"
          >
            <Code className="size-4 text-gray-500" />
          </Button>
          {!selectedImageFile && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => fileInputRef.current?.click()}
              disabled={isPending || isUploading}
              title="Thêm ảnh"
            >
              <ImageIcon className="size-6 text-gray-500" />
            </Button>
          )}
        </div>
      </div>

      <div className="relative">
        {showMentionDropdown && filteredUsers.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute bottom-full left-0 right-0 z-50 mb-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-48 overflow-y-auto"
          >
            <ScrollArea className="h-full">
              {filteredUsers.map((user, index) => (
                <div
                  key={user.id}
                  className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                    index === selectedUserIndex ? 'bg-blue-50' : ''
                  }`}
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => handleInsertMention(user)}
                >
                  <Avatar className="h-6 w-6">
                    <ResolvedAvatarImage avatar={user.avatar} alt={user.displayName} />
                    <AvatarFallback className="text-xs">{user.displayName.charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm text-gray-700 truncate">{user.displayName}</span>
                </div>
              ))}
            </ScrollArea>
          </div>
        )}

        <div className="flex gap-2">
          <UserAvatarPopover user={user} />
          <div className="flex-1">
            <div className="relative">
              <QuillWrapper
              ref={commentQuillRef}
              value={newComment}
              onChange={handleQuillChange}
              placeholder="Viết bình luận..."
              modules={{ toolbar: false, hashtag: {}, math: {}, codeBlock: {}, mention: {} }}
              formats={[
                'bold',
                'italic',
                'underline',
                'strike',
                'link',
                'blockquote',
                'hashtag',
                'customEmoji',
                'math',
                'codeBlock',
                'mention',
              ]}
              className="bg-gray-100 overflow-y-auto pr-10 pl-2 rounded-md min-h-[40px]"
              id="comment-quill"
              theme="snow"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                disabled={isPending || isUploading || !hasContent}
                onClick={() => submitComment()}
                title="Gửi bình luận"
              >
                {isPending || isUploading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-500"></div>
                ) : (
                  <Send className="h-4 w-4 text-gray-500" />
                )}
              </Button>
              <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageSelect} />
            </div>
            </div>

            {previewImage && (
              <div className="mt-2 relative rounded-lg overflow-hidden bg-gray-50">
                <Image
                  src={previewImage}
                  alt="Preview"
                  width={200}
                  height={150}
                  className="object-contain rounded-lg border border-gray-200 w-full max-h-40"
                />
                <div className="absolute top-2 right-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 bg-gray-100 hover:bg-gray-200 rounded-full"
                    onClick={removeImage}
                    disabled={isPending || isUploading}
                  >
                    <X className="h-3 w-3 text-gray-500" />
                  </Button>
                </div>
                {isUploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg">
                    <div className="flex flex-col items-center text-white">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white mb-2"></div>
                      <span className="text-sm">Đang tải ảnh...</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <MathDialog
        isOpen={isMathDialogOpen}
        onClose={() => {
          setIsMathDialogOpen(false)
          setEditingMathNode(null)
          setEditingMathLatex('')
        }}
        onInsert={handleMathInsert}
        initialValue={editingMathLatex}
        isEditMode={!!editingMathNode}
      />

      <CodeBlockDialog
        isOpen={isCodeBlockDialogOpen}
        onClose={() => setIsCodeBlockDialogOpen(false)}
        onInsert={handleCodeBlockInsert}
      />
      <style jsx>{`
        #comment-quill .ql-editor {
          font-size: 16px !important;
        }

        #comment-quill .ql-editor.ql-blank::before {
          left: 5px !important;
        }

        #comment-quill .ql-editor .mention {
          background-color: #e2e8f0 !important;
          border-radius: 4px !important;
          padding: 2px 4px !important;
          color: #1e293b !important;
          font-weight: 500 !important;
          cursor: pointer !important;
          white-space: nowrap !important;
          display: inline-block !important;
          user-select: none !important;
          margin: 0 !important;
          transition: background-color 0.2s ease !important;
        }

        #comment-quill .ql-editor .mention:hover {
          background-color: #cbd5e1 !important;
          text-decoration: none !important;
        }

        /* Ensure mentions don't get mixed with normal text formatting */
        #comment-quill .ql-editor .mention * {
          background: inherit !important;
          color: inherit !important;
          font-weight: inherit !important;
        }

        /* Hashtag styling */
        #comment-quill .ql-editor .hashtag {
          background-color: #dbeafe !important;
          color: #1e40af !important;
          border-radius: 4px !important;
          padding: 2px 4px !important;
          font-weight: 500 !important;
          cursor: pointer !important;
          white-space: nowrap !important;
          display: inline-block !important;
          user-select: none !important;
          margin: 0 1px !important;
        }

        #comment-quill .ql-editor .hashtag:hover {
          background-color: #bfdbfe !important;
        }

        /* Ensure hashtags don't get mixed with normal text formatting */
        #comment-quill .ql-editor .hashtag * {
          background: inherit !important;
          color: inherit !important;
          font-weight: inherit !important;
        }
      `}</style>
    </div>
  )
}

function PublisherRow({ currentPost, onEdit }: { currentPost: MemePost; onEdit?: (post: MemePost) => void }) {
  const { user } = useAuth()
  const { closeModal } = useMemePostModal('MemePostModal')
  const queryClient = useQueryClient()
  const searchParams = useSearchParams()
  const [isDeleting, setIsDeleting] = React.useState(false)

  const channelId = currentPost.channel?.id ?? searchParams.get('channelId') ?? undefined
  const canPin = isAdmin(user) || isInstructor(user)
  const isOwner = user?.id === currentPost.user.id
  const canDelete = isOwner || isAdmin(user)

  const { data: pinnedPosts } = useQuery({
    queryKey: ['pinned-posts', channelId],
    queryFn: () => {
      if (!channelId) return Promise.resolve([])
      return memeService.getPinnedPosts(channelId)
    },
    enabled: !!channelId,
  })

  const isPinned = React.useMemo(
    () => (pinnedPosts ?? []).some((pinned: PinnedPost) => pinned.post.id === currentPost.id),
    [pinnedPosts, currentPost.id]
  )

  const handlePin = async () => {
    if (!channelId) return
    try {
      await memeService.pinPost(channelId, currentPost.id)
      queryClient.invalidateQueries({ queryKey: ['pinned-posts', channelId] })
      toast({ title: 'Thành công', description: 'Đã ghim bài viết' })
    } catch {
      toast({ title: 'Lỗi', description: 'Không thể ghim bài viết', variant: 'destructive' })
    }
  }

  const handleUnpin = async () => {
    if (!channelId) return
    try {
      await memeService.unpinPost(channelId, currentPost.id)
      queryClient.invalidateQueries({ queryKey: ['pinned-posts', channelId] })
      toast({ title: 'Thành công', description: 'Đã bỏ ghim bài viết' })
    } catch {
      toast({ title: 'Lỗi', description: 'Không thể bỏ ghim bài viết', variant: 'destructive' })
    }
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await memeService.deleteMeme(currentPost.id)
      queryClient.invalidateQueries({ queryKey: ['meme-post-list'] })
      closeModal()
      toast({ title: 'Thành công', description: 'Đã xóa bài viết' })
    } catch {
      toast({ title: 'Lỗi', description: 'Không thể xóa bài viết', variant: 'destructive' })
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="flex items-start gap-3">
      <Link href={`/user/${currentPost?.user.profileId}`} onClick={() => closeModal()}>
        <Avatar className="h-10 w-10">
          <ResolvedAvatarImage avatar={currentPost?.user.avatar} alt={currentPost?.user.firstName || 'User'} />
          <AvatarFallback>{currentPost?.isAnonymous ? 'A' : 'OP'}</AvatarFallback>
        </Avatar>
      </Link>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-sm">
              <Link href={`/user/${currentPost?.user.profileId}`} onClick={() => closeModal()}>
                {getDisplayName({ firstName: currentPost?.user.firstName, lastName: currentPost?.user.lastName })}
              </Link>
            </h3>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <span>•</span>
              <span>
                {formatDistanceToNow(currentPost?.createdAt || new Date(), {
                  addSuffix: true,
                  locale: vi,
                })}
              </span>
              {user?.id === currentPost?.user.id && (
                <Badge variant="outline" className="text-blue-600 border-blue-600">
                  Me
                </Badge>
              )}
            </div>
          </div>
          {canPin || isOwner || canDelete ? (
            <PostOptionsDropdown
              showPin={canPin && !!channelId && !isPinned}
              showUnpin={canPin && !!channelId && isPinned}
              showEdit={isOwner && Boolean(onEdit)}
              showDelete={canDelete}
              isDeleting={isDeleting}
              onPin={handlePin}
              onUnpin={handleUnpin}
              onEdit={onEdit ? () => onEdit(currentPost) : undefined}
              onDelete={handleDelete}
              triggerClassName="h-8 w-8"
              iconClassName="h-4 w-4"
              contentClassName="!z-[100000]"
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

function ActionButtons() {
  const { currentPost, setCurrentPost } = useMemePostModal('MemePostModal')
  const [isReacting, setIsReacting] = React.useState(false)
  const { isAuthenticated, user } = useAuth()
  const queryClient = useQueryClient()

  const seoData = currentPost ? generateMemeSEO(currentPost) : null
  const shareUrl = currentPost ? `${window.location.origin}/forum/shared/${currentPost.id}` : ''

  const handleToggleReaction = async (postId: string, reactionType = 'like', emojiData?: EmojiData) => {
    if (!isAuthenticated) {
      // You could show a login modal or toast here
      // eslint-disable-next-line no-console
      console.warn('User must be authenticated to react')
      return
    }

    setIsReacting(true)
    try {
      // Make the API call
      await memeService.toggleReaction({ postId, reactionType, emojiData })
      const post = await memeService.getMemeDetail(postId)
      setCurrentPost(post)
      queryClient.invalidateQueries({ queryKey: ['meme-post-list'] })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to toggle reaction:', error)
    } finally {
      setIsReacting(false)
    }
  }

  const handleRemoveCurrentUserReaction = async (postId: string) => {
    try {
      await memeService.removePostReaction(postId)
      const post = await memeService.getMemeDetail(postId)
      setCurrentPost(post)
      queryClient.invalidateQueries({ queryKey: ['meme-post-list'] })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to remove current user reaction:', error)
    }
  }

  return (
    <div className="flex gap-2 border-b border-t py-1 !mt-0">
      {currentPost && (
        <MemePostReactions
          postId={currentPost?.id}
          reactions={currentPost?.reactions || []}
          onReact={handleToggleReaction}
          currentUser={user}
          isAuthenticated={isAuthenticated}
          isReacting={isReacting}
          modal={true}
          className="flex-1"
          onRemoveCurrentUserReaction={handleRemoveCurrentUserReaction}
        />
      )}
      <Button variant="ghost" className="flex items-center gap-2 py-2 text-gray-600 hover:bg-gray-100 flex-1">
        <MessageCircle className="h-4 w-4" />
        <span className="text-sm">Bình luận</span>
      </Button>
      {/* <ShareButton
        variant="ghost"
        className="flex items-center gap-2 py-2 text-gray-600 hover:bg-gray-100 flex-1"
        textContent="Chia sẻ"
        shareUrl={shareUrl}
        title={seoData?.title}
        description={seoData?.description}
        toastProps={{
          description: 'Copied to clipboard',
          title: 'Share this meme',
        }}
      >
        <Share2 className="h-4 w-4" />
      </ShareButton> */}

      {/* Enhanced Social Share Buttons */}
      {seoData && (
        <SocialShareButtons
          url={shareUrl}
          title={seoData.title}
          description={seoData.description}
          id={currentPost?.id}
          type="post"
          hashtags={seoData.tags}
          variant="ghost"
          size="sm"
          className="flex-1"
          textContent={<span className="hidden sm:inline">Chia sẻ</span>}
        />
      )}
    </div>
  )
}

function CaptionBlock({ currentPost }: { currentPost: MemePost }) {
  const { closeModal } = useMemePostModal('MemePostModal')
  return (
    <div className="space-y-2">
      <div className="ql-container ql-snow">
        <ClickableHashtagContent content={currentPost?.content || ''} className="text-sm" onHashtagClick={closeModal} />
      </div>
    </div>
  )
}

export default function PostDetailModal() {
  const { isOpen, closeModal, currentPost } = useMemePostModal('MemePostModal')
  const [editingPost, setEditingPost] = React.useState<MemePost | null>(null)

  const handleEditPost = React.useCallback(
    (post: MemePost) => {
      setEditingPost(post)
      closeModal()
    },
    [closeModal]
  )

  return (
    <>
      {editingPost ? (
        <MemePostEditorContainer editPost={editingPost} onCloseEdit={() => setEditingPost(null)} />
      ) : null}
      {isOpen && currentPost?.id ? (
        <ViewTracker entityType="post-meme" entityId={currentPost.id} windowHours={12} />
      ) : null}
      {currentPost && (
        <SEO
          title={
            currentPost?.content
              ? currentPost.content.replace(/<[^>]*>/g, '').substring(0, 100) +
                (currentPost.content.length > 100 ? '...' : '')
              : 'Meme Post'
          }
          description={`Meme by ${currentPost?.user.firstName || ''} ${currentPost?.user.lastName || 'FSDS Community'}`}
          ogType="article"
          section="Community"
          tags={['meme', 'community', 'data science', 'fun', 'AI']}
          keywords="meme, community, data science, machine learning, AI, fun, FSDS"
          author={currentPost?.user.firstName + ' ' + currentPost?.user.lastName}
          publishedTime={currentPost?.createdAt?.toString()}
          modifiedTime={currentPost?.updatedAt?.toString()}
          ogImage={currentPost?.images?.[0]?.imageUrl || ''}
        />
      )}
      <Dialog
        open={isOpen}
        onOpenChange={open => {
          if (!open) {
            closeModal()
          }
        }}
      >
        <DialogContent className="max-w-screen-md max-h-[calc(100vh-2rem)] sm:max-h-[90vh] p-0 gap-0 overflow-hidden flex flex-col">
          {/* Zone 1 & 2: Modal Shell + Header Bar */}
          <DialogHeader className="flex flex-row items-center justify-between p-4 border-b bg-white flex-shrink-0">
            <DialogTitle className="text-sm font-semibold text-gray-900">
              {currentPost
                ? `Bài viết của ${currentPost.user.firstName + ' ' + currentPost.user.lastName}`
                : 'Bài viết'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            <div className="p-4 space-y-3 pb-5">
              {/* All existing content from Zone 3 to Zone 8 stays the same */}
              {/* Zone 3: Publisher Row */}
              {currentPost && <PublisherRow currentPost={currentPost} onEdit={handleEditPost} />}
              {/* Zone 4: Caption Block */}
              {currentPost && <CaptionBlock currentPost={currentPost} />}
              {/* Zone 5: Media Section */}
              <div className="relative">
                {currentPost?.images.length ? (
                  <ImageMemeSlideShow slides={currentPost?.images.map(img => ({ alt: img.id, src: img.imageUrl }))} />
                ) : null}
              </div>
              {/* Zone 6: Engagement Summary Bar */}
              {currentPost && <EngagementSummaryBar post={currentPost} className="pr-14 pl-4" />}
              {/* Zone 7: Action Buttons */}
              {currentPost && <ActionButtons />}
              {/* Zone 8: Comments Area */}
              <div className="space-y-4">{currentPost && <CommentArea postId={currentPost.id} />}</div>
            </div>
          </div>

          {/* Zone 9: Fixed Comment Composer */}
          {currentPost && <CommentComposer currentPost={currentPost} />}
        </DialogContent>
      </Dialog>
    </>
  )
}
